import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Satellite, Globe, Ship, Waves, Wind, BarChart3, ArrowRight, Check, Users, Building2, Anchor } from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

const Innovation = () => {
  const { t, i18n } = useTranslation('innovation');

  const pillars = [
    { icon: Satellite, title: t('pillars.satellite'), description: t('pillars.satelliteDesc') },
    { icon: Globe, title: t('pillars.gnss'), description: t('pillars.gnssDesc') },
    { icon: Wind, title: t('pillars.satcom'), description: t('pillars.satcomDesc') },
  ];

  const benefits = [
    { icon: Ship, title: t('benefits.voyage'), description: t('benefits.voyageDesc') },
    { icon: BarChart3, title: t('benefits.insight'), description: t('benefits.insightDesc') },
    { icon: Waves, title: t('benefits.emissions'), description: t('benefits.emissionsDesc') },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
        <link rel="canonical" href="https://cetasol.com/technology" />
        <meta property="og:title" content={t('meta.title')} />
        <meta property="og:description" content={t('meta.description')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cetasol.com/technology" />
        <meta property="og:image" content="https://cetasol.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('meta.title')} />
        <meta name="twitter:description" content={t('meta.description')} />
        <meta name="twitter:image" content="https://cetasol.com/og-image.jpg" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://cetasol.com/" },
            { "@type": "ListItem", "position": 2, "name": "Innovation", "item": "https://cetasol.com/technology" }
          ]
        })}</script>
      </Helmet>
      <Header />
      <main id="main-content">
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
          <div className="container mx-auto px-6 relative">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
              <p className="text-xs font-semibold tracking-widest uppercase text-section-label mb-4">{t('hero.label')}</p>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">CetaOrbit</h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-4">{t('hero.description')}</p>
              <p className="text-sm text-muted-foreground">{t('hero.fundedBy')}</p>
            </motion.div>
          </div>
        </section>

        <section className="py-20 border-t border-border">
          <div className="container mx-auto px-6">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={fadeUp} custom={0} className="max-w-3xl mb-12">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-6">{t('atGlance.heading')}</h2>
              <p className="text-muted-foreground leading-relaxed">{t('atGlance.description')}</p>
            </motion.div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-8 max-w-5xl border-t border-border pt-10">
              {(['programme','type','lead','focus'] as const).map((key, i) => (
                <motion.div key={key} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={fadeUp} custom={i}>
                  <p className="text-xs font-semibold tracking-widest uppercase text-section-label mb-2">{t(`atGlance.facts.${key}`)}</p>
                  <p className="text-foreground leading-relaxed">{t(`atGlance.facts.${key}Value`)}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 border-t border-border">
          <div className="container mx-auto px-6">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={fadeUp} custom={0} className="max-w-3xl">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-6">{t('challenge.heading')}</h2>
              <p className="text-muted-foreground leading-relaxed">{t('challenge.description')}</p>
            </motion.div>
          </div>
        </section>



        <section className="py-20 bg-secondary/30">
          <div className="container mx-auto px-6">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={fadeUp} custom={0} className="max-w-3xl mb-16">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-6">{t('whyItMatters.heading')}</h2>
              <p className="text-muted-foreground leading-relaxed">{t('whyItMatters.description')}</p>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-8">
              {pillars.map((pillar, i) => (
                <motion.div key={pillar.title} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={fadeUp} custom={i + 1} className="bg-card border border-border rounded-lg p-8">
                  <pillar.icon className="h-8 w-8 text-primary mb-4" />
                  <h3 className="font-display text-lg font-bold text-foreground mb-3">{pillar.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{pillar.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-6">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={fadeUp} custom={0} className="max-w-3xl mb-16">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-6">{t('howItWorks.heading')}</h2>
              <p className="text-muted-foreground leading-relaxed">{t('howItWorks.description')}</p>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-8">
              {benefits.map((benefit, i) => (
                <motion.div key={benefit.title} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={fadeUp} custom={i + 1} className="flex gap-4">
                  <div className="flex-shrink-0 h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <benefit.icon className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold text-foreground mb-2">{benefit.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{benefit.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 border-t border-border">
          <div className="container mx-auto px-6">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={fadeUp} custom={0} className="max-w-3xl mb-12">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-6">{t('audience.heading')}</h2>
              <p className="text-muted-foreground leading-relaxed">{t('audience.description')}</p>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-10 max-w-5xl border-t border-border pt-10">
              {([
                { key: 'small', icon: Anchor },
                { key: 'medium', icon: Ship },
                { key: 'regulators', icon: Building2 },
              ] as const).map((g, i) => (
                <motion.div key={g.key} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={fadeUp} custom={i}>
                  <g.icon className="h-7 w-7 text-primary mb-4" />
                  <h3 className="font-display text-lg font-bold text-foreground mb-2">{t(`audience.groups.${g.key}`)}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{t(`audience.groups.${g.key}Desc`)}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 border-t border-border">
          <div className="container mx-auto px-6">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={fadeUp} custom={0} className="max-w-3xl mb-12">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-6">{t('projectScope.heading')}</h2>
              <p className="text-muted-foreground leading-relaxed">{t('projectScope.description')}</p>
            </motion.div>
            <ol className="max-w-4xl divide-y divide-border border-t border-border">
              {(['p1','p2','p3','p4','p5'] as const).map((p, i) => (
                <motion.li key={p} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={fadeUp} custom={i} className="py-6 grid grid-cols-[auto,1fr] gap-6 items-baseline">
                  <span className="font-display text-2xl font-bold text-primary tabular-nums">0{i + 1}</span>
                  <div>
                    <h3 className="font-display text-lg font-bold text-foreground mb-1">{t(`projectScope.phases.${p}`)}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{t(`projectScope.phases.${p}Desc`)}</p>
                  </div>
                </motion.li>
              ))}
            </ol>
          </div>
        </section>

        <section className="py-20 border-t border-border">
          <div className="container mx-auto px-6">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={fadeUp} custom={0} className="max-w-3xl mb-10">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-6">{t('outcomes.heading')}</h2>
            </motion.div>
            <ul className="max-w-4xl space-y-4">
              {(['decision','savings','coverage','compliance'] as const).map((k, i) => (
                <motion.li key={k} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={fadeUp} custom={i} className="flex gap-3 items-start">
                  <Check className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                  <span className="text-muted-foreground leading-relaxed">{t(`outcomes.items.${k}`)}</span>
                </motion.li>
              ))}
            </ul>
          </div>
        </section>



        <section className="py-20 bg-secondary/30">
          <div className="container mx-auto px-6">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={fadeUp} custom={0} className="max-w-3xl">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-6">{t('sustainability.heading')}</h2>
              <p className="text-muted-foreground leading-relaxed mb-6">{t('sustainability.p1')}</p>
              <p className="text-muted-foreground leading-relaxed">{t('sustainability.p2')}</p>
            </motion.div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-6 text-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={fadeUp} custom={0}>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">{t('cta.heading')}</h2>
              <p className="text-muted-foreground mb-8 max-w-xl mx-auto">{t('cta.description')}</p>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3 rounded-md font-medium hover:bg-primary-hover transition-colors">
                {t('cta.heading').includes('CetaOrbit') ? (i18n.language === 'sv' ? 'Kontakta oss' : 'Contact us') : 'Contact us'}
                <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Innovation;
